import { SUPABASE_URL } from '../../../utils/supabase/url';
import type {
  Database,
  EmailSignature,
  WorkingSchedule,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

export const withUsers = <TBase extends Constructor<any>>(Base: TBase) => class Users extends Base {
  // USERS
  // ============================================================================

  public getUsers(): User[] {
    return Object.values(this.db.users);
  }

  public getUser(id: string): User | undefined {
    return this.db.users[id];
  }

  public getUsersByCompany(companyId: string): User[] {
    return this.getUsers().filter(u => (u.companyId || (u as any).company_id) === companyId);
  }

  public async createUser(user: Omit<User, 'id' | 'created_at' | 'updated_at'>): Promise<User> {
    // passwordHash is set by the server (Supabase Auth). Preserve any value the
    // caller has already supplied (migration path), but don't generate one here.
    const newUser: User = {
      ...user,
      id: this.generateId(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as User;
    
    this.db.users[newUser.id] = newUser;
    this.saveToLocalStorage();
    this.saveEntityToBackend('users', newUser.id, newUser, true);
    
    // Log system activity for user creation
    const company = this.getCompany(newUser.companyId);
    const currentUser = this.getUsers().find(u => u.email === 'gary@contractgroup.co.uk');
    
    this.logActivity({
      type: 'user_created',
      category: 'user',
      action: 'User account created',
      detail: `${newUser.name} (${newUser.email})`,
      userId: currentUser?.id || 'system',
      userName: currentUser?.name || 'System',
      userEmail: currentUser?.email || 'system@contractgroup.co.uk',
      metadata: {
        userId: newUser.id,
        userName: newUser.name,
        userEmail: newUser.email,
        userRole: newUser.role,
        companyId: newUser.companyId,
        companyName: company?.name,
      },
    });
    
    this.notifyListeners();
    return newUser;
  }

  public async createUserAsync(user: Omit<User, 'id' | 'created_at' | 'updated_at'>): Promise<User> {
    // passwordHash is server-managed (Supabase Auth). Keep any pre-hashed value
    // from migration callers; otherwise omit so the server sets it.
    const newUser: User = {
      ...user,
      id: this.generateId(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as User;

    this.db.users[newUser.id] = newUser;
    this.saveToLocalStorage();

    try {
      await this.saveEntityToBackend('users', newUser.id, newUser, true);

      const company = this.getCompany(newUser.companyId);
      const currentUser = this.getUsers().find(u => u.email === 'gary@contractgroup.co.uk');

      this.logActivity({
        type: 'user_created',
        category: 'user',
        action: 'User account created',
        detail: `${newUser.name} (${newUser.email})`,
        userId: currentUser?.id || 'system',
        userName: currentUser?.name || 'System',
        userEmail: currentUser?.email || 'system@contractgroup.co.uk',
        metadata: {
          userId: newUser.id,
          userName: newUser.name,
          userEmail: newUser.email,
          userRole: newUser.role,
          companyId: newUser.companyId,
          companyName: company?.name,
        },
      });
    } catch (error) {
      delete this.db.users[newUser.id];
      this.saveToLocalStorage();
      this.notifyListeners();
      throw error;
    }

    this.notifyListeners();
    return newUser;
  }

  /** Save only signatures; unrelated account fields may require different permissions. */
  public async updateUserEmailSignatures(id: string, signatures: EmailSignature[]): Promise<void> {
    if (!this.db.users[id]) throw new Error('User not found. Reload and try again.');

    if (this.useSupabase) {
      const token = await this.getAuthHeaderToken();
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_signatures: signatures }),
      });
      if (!response.ok) throw new Error(`Signature was not saved (${response.status}). Your draft is still open.`);
    }

    // Read the latest cache after persistence so concurrent profile edits are preserved.
    this.db.users[id] = { ...this.db.users[id], emailSignatures: signatures };
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  /** Writes only working_schedule and timezone. updateUser sends the whole cached users.preferences blob, which can
   *  revert a key another surface wrote since the cache loaded (the theme mirror), so schedule edits avoid it. */
  public async updateUserWorkingSchedule(id: string, schedule: WorkingSchedule, options?: {throwOnPersistError?: boolean; isCurrent?: () => boolean}): Promise<void> {
    if (!this.db.users[id]) {
      if (options?.throwOnPersistError) throw new Error('Your account could not be checked. Reload before saving working hours.');
      return;
    }
    // Availability needs confirmed saves; existing fire-and-forget callers keep their optimistic contract.
    if (options?.throwOnPersistError) {
      const isCurrent = options.isCurrent ?? (() => true);
      if (!isCurrent()) throw new Error('Your account changed. Reopen Availability before saving.');
      if (this.useSupabase) {
        const token = await this.getAuthHeaderToken();
        if (!isCurrent()) throw new Error('Your account changed. Reopen Availability before saving.');
        const response = await fetch(`${API_BASE}/users/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ working_schedule: schedule, ...(schedule.timezone ? { timezone: schedule.timezone } : {}) }),
        });
        if (!response.ok) throw new Error(`Working hours were not saved (${response.status}). Your changes are kept here.`);
      }
      if (!isCurrent()) throw new Error('Your account changed. Reopen Availability to check the saved hours.');
      const latest = this.db.users[id];
      if (!latest) throw new Error('Your account could not be checked. Reload before saving working hours.');
      this.db.users[id] = {...latest, ...(schedule.timezone ? {timezone: schedule.timezone} : {}), preferences: {...(latest.preferences ?? {}), workingSchedule: schedule}, updatedAt: new Date().toISOString()};
      this.saveToLocalStorage();
      this.notifyListeners();
      return;
    }
    const timezone = schedule.timezone || null;

    this.db.users[id] = {
      ...this.db.users[id],
      ...(timezone ? { timezone } : {}),
      preferences: { ...(this.db.users[id].preferences ?? {}), workingSchedule: schedule },
      updatedAt: new Date().toISOString(),
    };
    this.saveToLocalStorage();
    this.notifyListeners();

    if (!this.useSupabase) return;
    try {
      const token = await this.getAuthHeaderToken();
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ working_schedule: schedule, ...(timezone ? { timezone } : {}) }),
      });
      if (!response.ok) console.error(`[updateUserWorkingSchedule] PUT failed for user ${id} (${response.status})`);
    } catch (error) {
      console.error(`[updateUserWorkingSchedule] Network error for user ${id}:`, error);
    }
  }

  /** `throwOnPersistError` is opt-in because most callers here are
   *  fire-and-forget: making this reject unconditionally would turn every one
   *  of them into an unhandled rejection. Pass it wherever the UI claims the
   *  save succeeded, or the claim is unverified. */
  public async updateUser(
    id: string,
    updates: Partial<User>,
    opts?: { throwOnPersistError?: boolean },
  ): Promise<User | undefined> {
    const user = this.db.users[id];
    if (!user) return undefined;

    // CALLER INVARIANT: pass the CANONICAL camelCase key, or write every alias.
    // This merge is raw — it does not canonicalise. transformUserForBackend
    // accepts aliases (`position || jobTitle`, `company_id || companyId`, and
    // the camel/snake pairs), so the BACKEND always ends up correct, but the
    // local cache only gets the key you passed. Readers use the canonical key
    // the load path produces, so writing an alias leaves the value they read
    // stale until the next resync — the save looks like it silently failed.
    // This has bitten three times: LoginPage and ContactProfilePanel both
    // dual-write to work around it, and EditUserForm's Position field was
    // broken by it. Canonicalising here would fix it for everyone; it is not
    // done yet because this merge is shared by every updateUser caller.
    const updated = {
      ...user,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };

    // Always update locally first so the UI responds instantly
    this.db.users[id] = updated;
    this.saveToLocalStorage();
    this.notifyListeners();

    // Then persist to backend in the background using the same auth path as saveEntityToBackend
    if (this.useSupabase) {
      try {
        const transformedData = this.transformDataForBackend('users', updated);
        const token = await this.getAuthHeaderToken();

        const response = await fetch(`${API_BASE}/users/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(transformedData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ [updateUser] Backend PUT failed for user ${id}:`, {
            status: response.status,
            statusText: response.statusText,
            errorText,
            sentFields: Object.keys(transformedData),
          });
          if (opts?.throwOnPersistError) {
            throw new Error(`Failed to save (${response.status})`);
          }
        } else {
          console.log(`✅ [updateUser] User ${id} persisted to backend`);
        }
      } catch (error) {
        console.error(`❌ [updateUser] Network/transform error for user ${id}:`, error);
        if (opts?.throwOnPersistError) throw error;
      }
    }

    return updated;
  }

  /**
   * Persist a per-chat message workflow state (saved/pinned/follow-up message IDs)
   * to the dedicated user_message_workflow_states table instead of the preferences blob.
   * Also updates the in-memory preferences.messageWorkflowStates for immediate consistency.
   */
  public async updateMessageWorkflowState(
    userId: string,
    chatId: string,
    state: { savedMessageIds: string[]; pinnedDecisionIds: string[]; followUpMessageIds: string[]; updatedAt?: string }
  ): Promise<void> {
    // Update in-memory immediately so reads are consistent
    const user = this.db.users[userId];
    if (user) {
      this.db.users[userId] = {
        ...user,
        preferences: {
          ...(user.preferences || {}),
          messageWorkflowStates: {
            ...(user.preferences?.messageWorkflowStates || {}),
            [chatId]: state,
          },
        },
      };
      this.notifyListeners();
    }

    if (!this.useSupabase) return;

    try {
      // Route requires the caller's JWT (it checks the user can only write
      // their own states) — the anon key gets a 401 before the handler runs.
      const token = await this.getAuthHeaderToken();
      await fetch(`${API_BASE}/users/${userId}/message-workflow-states/${chatId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(state),
      });
    } catch (error) {
      console.warn('[DataService] Failed to persist message workflow state to server:', error);
    }
  }

  public async deleteUser(id: string): Promise<boolean> {
    if (!this.db.users[id]) return false;

    // BACKEND-FIRST: Delete from backend FIRST
    if (this.useSupabase) {
      try {
        const response = await fetch(`${API_BASE}/users/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Backend delete failed:`, response.status, errorText);
          throw new Error(`Backend delete failed: ${response.status}`);
        }
        
        console.log(`✅ User deleted from backend:`, id);
        
        // Only delete locally after successful backend delete
        delete this.db.users[id];
        this.saveToLocalStorage();
        this.notifyListeners();
        return true;
      } catch (error) {
        console.error(`❌ Error deleting user from backend:`, error);
        throw error;
      }
    } else {
      // Fallback: If not using Supabase, just delete locally
      delete this.db.users[id];
      this.saveToLocalStorage();
      this.notifyListeners();
      return true;
    }
  }

  // ============================================================================
};
